import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { rosterAPI, seasonsAPI, teamsAPI } from '../../api/api';
import PlayerAvatar from '../../components/PlayerAvatar';
import { useAuth } from '../../context/AuthContext';

// Kaderverwaltung: GM setzt Spieler seines Teams auf inaktiv. Inaktive bleiben
// im Kader (SeasonRosters), verschwinden aber aus allen öffentlichen Ansichten
// und aus der Statistikerfassung — sichtbar sind sie nur noch hier.
const ROSTER_TARGET = 15;

const RosterManagement = () => {
  const { user, isAdmin } = useAuth();
  const { t } = useTranslation();
  const admin = isAdmin();

  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(admin ? '' : (user?.teamId || ''));
  const [activeSeason, setActiveSeason] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  // Teams (nur für Admins zur Auswahl) und aktive Saison einmalig laden
  useEffect(() => {
    const loadBase = async () => {
      try {
        const requests = [seasonsAPI.getActive().catch(() => ({ data: null }))];
        if (admin) requests.push(teamsAPI.getAll());
        const [seasonRes, teamsRes] = await Promise.all(requests);
        setActiveSeason(seasonRes.data || null);
        if (teamsRes) {
          const sorted = [...teamsRes.data].sort((a, b) =>
            `${a.city} ${a.name}`.localeCompare(`${b.city} ${b.name}`)
          );
          setTeams(sorted);
          if (sorted.length > 0) setSelectedTeam(String(sorted[0].id));
        }
      } catch (e) {
        setError(t('roster.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    loadBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  const loadRoster = useCallback(async (teamId) => {
    if (!teamId) {
      setPlayers([]);
      return;
    }
    setRosterLoading(true);
    try {
      const res = await rosterAPI.getTeamRoster(teamId, activeSeason?.id);
      setPlayers(res.data || []);
      setError(null);
    } catch (e) {
      setPlayers([]);
      setError(e.response?.data?.error || t('roster.loadFailed'));
    } finally {
      setRosterLoading(false);
    }
  }, [activeSeason, t]);

  useEffect(() => {
    if (!loading && selectedTeam) loadRoster(selectedTeam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, selectedTeam, activeSeason]);

  const toggleBenched = async (player) => {
    const nextBenched = !player.isBenched;
    const message = nextBenched ? t('bench.confirmDeactivate') : t('bench.confirmActivate');
    if (!window.confirm(message)) return;

    setSavingId(player.id);
    try {
      await rosterAPI.setBenched(selectedTeam, player.id, nextBenched);
      setPlayers(prev => prev.map(p => (p.id === player.id ? { ...p, isBenched: nextBenched } : p)));
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || t('bench.updateFailed'));
    } finally {
      setSavingId(null);
    }
  };

  const byRating = (a, b) => {
    const diff = (b.overallRating || 0) - (a.overallRating || 0);
    if (diff !== 0) return diff;
    return (a.lastName || '').localeCompare(b.lastName || '');
  };
  const activePlayers = players.filter(p => !p.isBenched).sort(byRating);
  const benchedPlayers = players.filter(p => p.isBenched).sort(byRating);
  const overTarget = activePlayers.length > ROSTER_TARGET;

  const renderRow = (player) => (
    <tr
      key={player.id}
      className={`border-b dark:border-gray-700 ${player.isBenched ? 'opacity-60' : ''}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <PlayerAvatar player={player} size="small" />
          <div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {player.firstName} {player.lastName}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              #{player.jerseyNumber || '-'} · {player.position}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`text-lg font-bold ${
          player.overallRating >= 85 ? 'text-green-600 dark:text-green-400' :
          player.overallRating >= 80 ? 'text-blue-600 dark:text-blue-400' :
          player.overallRating >= 75 ? 'text-yellow-600' :
          'text-gray-600 dark:text-gray-400'
        }`}>
          {player.overallRating}
        </span>
      </td>
      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400">
        {player.badges || '-'}
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          player.isBenched
            ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
        }`}>
          {player.isBenched ? t('bench.inactiveBadge') : t('bench.activeBadge')}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <button
          onClick={() => toggleBenched(player)}
          disabled={savingId === player.id}
          className={`px-3 py-1 rounded text-xs font-semibold transition disabled:opacity-40 ${
            player.isBenched
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200'
          }`}
        >
          {savingId === player.id
            ? '⏳'
            : (player.isBenched ? t('bench.activate') : t('bench.deactivate'))}
        </button>
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (!admin && !user?.teamId) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded">
        <p className="text-sm text-yellow-800 dark:text-yellow-300">{t('roster.noTeam')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Kopf */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-2">
          👥 {t('roster.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{t('roster.intro')}</p>

        <div className="flex flex-wrap items-end gap-4">
          {!admin && user?.team && (
            <div className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
              🏟️ {user.team.city} {user.team.name}
            </div>
          )}

          {admin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('roster.selectTeam')}
              </label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 dark:bg-gray-700 dark:text-white"
              >
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.city} {team.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={`px-4 py-2 rounded-lg text-sm font-semibold ${
            overTarget
              ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
              : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
          }`}>
            {t('bench.activeCount', { active: activePlayers.length, benched: benchedPlayers.length })}
            {overTarget && ` · ${t('roster.overTarget', { target: ROSTER_TARGET })}`}
          </div>

          {activeSeason && (
            <div className="px-4 py-2 rounded-lg text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
              🏀 {activeSeason.name}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-red-600 dark:text-red-400 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-3 underline">
            {t('roster.dismiss')}
          </button>
        </div>
      )}

      {/* Kaderliste */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {rosterLoading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
        ) : players.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('roster.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('roster.player')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">OVR</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('roster.badges')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('roster.status')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('roster.action')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {activePlayers.map(renderRow)}
                {benchedPlayers.length > 0 && (
                  <tr className="bg-gray-100 dark:bg-gray-700/60">
                    <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300">
                      {t('bench.benchedSection', { count: benchedPlayers.length })} — {t('roster.benchedNote')}
                    </td>
                  </tr>
                )}
                {benchedPlayers.map(renderRow)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RosterManagement;
